import { usePxToRem } from '@b2b-ui/hooks/theme';
import { colors } from '@b2b-ui/theme/colors';
import { getETSUserCookiePayload } from '@b2b-ui/utilities/authentication';
import { getCurrentLocale } from '@b2b-ui/utilities/locales';
import MenuIcon from '@mui/icons-material/Menu';
import {
    Drawer,
    IconButton,
    Link,
    List,
    ListItem,
    ListSubheader,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import { Logo } from '../components/Logo';

import { getSections } from './menu-sections';

type SideNavProps = {
    onClickHelpCenter?: (event: React.MouseEvent) => void;
};

export const SideNav = ({ onClickHelpCenter }: SideNavProps) => {
    const [open, setOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const pxToRem = usePxToRem();
    const userInfo = getETSUserCookiePayload();
    const locale = getCurrentLocale();
    const fullName = userInfo?.userName || '';
    return (
        <>
            <IconButton
                data-testid="B2B-NavBar-open-drawer"
                sx={{
                    fontSize: pxToRem(24),
                    '&:hover': { backgroundColor: 'primary.light' },
                }}
                onClick={() => setOpen(true)}
                edge="start"
                color="inherit"
                aria-label="menu"
            >
                <MenuIcon fontSize="inherit" />
            </IconButton>
            <Drawer
                open={open}
                onClose={() => {
                    setOpen(false);
                }}
            >
                <Stack py={6} px={3} minWidth={300} gap={3}>
                    <Stack justifyContent="center" alignItems="center" gap={2}>
                        <Logo />
                        <Typography bgcolor="#f7f5ed" p={2} borderRadius="4px">
                            👋 {fullName}
                        </Typography>
                    </Stack>

                    <Stack gap={4}>
                        {getSections(locale).map((section, index) => {
                            return (
                                <List disablePadding key={index}>
                                    <ListSubheader
                                        sx={{ lineHeight: '20px' }}
                                        disableGutters
                                        key={section.label}
                                    >
                                        <Typography
                                            variant="overline"
                                            color="secondary.light"
                                        >
                                            {formatMessage({
                                                id: section.label,
                                            })}
                                        </Typography>
                                    </ListSubheader>

                                    {section.links.map((link) => {
                                        const isActive =
                                            window.location.pathname ===
                                            link.url;
                                        return (
                                            <ListItem
                                                component={Link}
                                                key={link.label}
                                                href={link.url}
                                                target="_blank"
                                                sx={{
                                                    borderRadius: '8px',
                                                    padding: 1.5,
                                                    '&:hover': {
                                                        backgroundColor:
                                                            colors.yellow[100],
                                                    },
                                                    ...(isActive
                                                        ? {
                                                              backgroundColor:
                                                                  colors
                                                                      .yellow[200],
                                                          }
                                                        : {}),
                                                }}
                                                rel="noreferrer"
                                                onClick={
                                                    link.url.includes(
                                                        'knowledge-base'
                                                    )
                                                        ? onClickHelpCenter
                                                        : undefined
                                                }
                                            >
                                                <Typography variant="body1">
                                                    {formatMessage({
                                                        id: link.label,
                                                    })}
                                                </Typography>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            );
                        })}
                    </Stack>
                </Stack>
            </Drawer>
        </>
    );
};
